import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircle2, Pause, Sparkles, ChevronDown, ChevronRight, ChevronLeft,
  Calendar, Award
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { studyPlanService, StudyPlan, DailyPlanProgress, PlanTask } from '../services/studyPlanService';
import { getLocalISOString } from '../utils/timezoneUtils';

// Mapeia rotas do aplicativo para os tipos de tarefas do roteiro de estudos
const ROUTE_TASK_TYPE_MAP: Record<string, string[]> = {
  '/flashcards': ['flashcards_words', 'flashcards_phrases'],
  '/pronunciation': ['pronunciation'],
  '/listening': ['listening'],
  '/writing': ['writing'],
  '/grammar': ['grammar_reading', 'grammar_quiz'],
  '/review-errors': ['review_errors'],
  '/correction': ['review_errors'],
  '/audiobooks': ['synced_audiobook']
};

export const MissionProgressBar: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [activePlan, setActivePlan] = useState<StudyPlan | null>(null);
  const [dailyProgress, setDailyProgress] = useState<DailyPlanProgress | null>(null);
  const [currentTask, setCurrentTask] = useState<PlanTask | null>(null);

  // Estados de progresso
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
  const [completedItemsCount, setCompletedItemsCount] = useState<number>(0);
  const [isIdle, setIsIdle] = useState<boolean>(false);
  const [justCompleted, setJustCompleted] = useState<boolean>(false);

  // Controle de expansão do painel de detalhes e minimização da barra
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isCollapsedRight, setIsCollapsedRight] = useState<boolean>(false);

  const lastActivityTimestampRef = useRef<number>(Date.now());
  // Design minimal v1: publica a altura visível da barra em --mission-h para o conteúdo não ficar por baixo dela
  const barRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = barRef.current;
    const root = document.documentElement;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const update = () => {
      const inner = el.firstElementChild as HTMLElement | null;
      const h = inner ? Math.round(inner.getBoundingClientRect().height) : 0;
      root.style.setProperty('--mission-h', `${h}px`);
    };
    const ro = new ResizeObserver(update);
    ro.observe(el);
    Array.from(el.children).forEach(c => ro.observe(c));
    update();
    return () => { ro.disconnect(); root.style.setProperty('--mission-h', '0px'); };
  });
  const timerIntervalRef = useRef<any>(null);
  const IDLE_THRESHOLD_MS = 20000; // 20 segundos de inatividade

  // Não renderizar na Home ("/") nem em páginas onde não há prática
  const isExcludedRoute = useMemo(() => {
    const p = location.pathname;
    return p === '/' || p.endsWith('-hub') || p === '/settings' || p === '/profile' || p === '/reports' || p === '/help';
  }, [location.pathname]);

  // Identificar se a rota atual corresponde a alguma tarefa do plano
  const matchingTaskType = useMemo(() => {
    const p = location.pathname.toLowerCase();
    for (const [routePrefix, taskTypes] of Object.entries(ROUTE_TASK_TYPE_MAP)) {
      if (p.startsWith(routePrefix)) {
        // Se estiver em flashcards, aceitar tanto words quanto phrases para qualquer baralho do dia
        if (routePrefix === '/flashcards') {
          const search = location.search.toLowerCase();
          if (search.includes('mode=phrases')) return 'flashcards_phrases';
          return 'flashcards_words';
        }
        if (routePrefix === '/grammar') {
          const search = location.search.toLowerCase();
          if (search.includes('view=quiz')) return 'grammar_quiz';
          return 'grammar_reading';
        }
        return taskTypes[0];
      }
    }
    return null;
  }, [location.pathname, location.search]);

  // 1. Carregar plano de estudos e progresso do dia (lendo idioma da URL se disponível ou fallback local)
  const loadPlanAndProgress = async () => {
    if (!user?.id || user.id === 'guest') return;

    try {
      const searchParams = new URLSearchParams(location.search);
      const urlLang = searchParams.get('lang');
      const activeLang = (urlLang ? urlLang.toLowerCase().split('-')[0] : null) || localStorage.getItem('libritui_active_study_lang') || 'en';
      
      const plan = await studyPlanService.getPlan(user.id, activeLang);
      if (!plan) return;
      setActivePlan(plan);

      const progress = await studyPlanService.getDailyProgress(user.id, activeLang, plan.current_day_index);
      if (progress) {
        setDailyProgress(progress);
      }
    } catch (e) {
      console.warn('[MissionProgressBar] Falha ao carregar plano:', e);
    }
  };

  useEffect(() => {
    loadPlanAndProgress();
  }, [user?.id, location.pathname, location.search]);

  useEffect(() => {
    const handlePlanUpdated = () => {
      loadPlanAndProgress();
    };
    window.addEventListener('study_plan_updated', handlePlanUpdated);
    return () => window.removeEventListener('study_plan_updated', handlePlanUpdated);
  }, [user?.id]);

  // 2. Determinar a tarefa ativa correspondente à tela atual
  useEffect(() => {
    if (!dailyProgress || !matchingTaskType) {
      setCurrentTask(null);
      return;
    }

    // Procura tarefa compatível que ainda não esteja concluída (ou pega a primeira do tipo correspondente)
    let resolvedTask = dailyProgress.tasks.find(t => t.type === matchingTaskType && !t.completed);
    if (!resolvedTask && (matchingTaskType === 'flashcards_words' || matchingTaskType === 'flashcards_phrases')) {
      resolvedTask = dailyProgress.tasks.find(t => (t.type === 'flashcards_words' || t.type === 'flashcards_phrases') && !t.completed);
    }
    if (!resolvedTask) {
      resolvedTask = dailyProgress.tasks.find(t => t.type === matchingTaskType) || null;
    }

    setCurrentTask(resolvedTask);

    if (resolvedTask) {
      const todayStr = getLocalISOString().split('T')[0];
      const planDay = activePlan?.current_day_index || 1;
      const cleanL = (activePlan?.language_code || 'en').toLowerCase().split('-')[0];

      const storageKeyTime = `libritui_task_time_${user?.id}_${cleanL}_${planDay}_${resolvedTask.id}`;
      const storageKeyItems = `libritui_task_items_${user?.id}_${cleanL}_${planDay}_${resolvedTask.id}`;
      const legacyKeyTime = `libritui_task_time_${user?.id}_${resolvedTask.id}_${todayStr}`;
      const legacyKeyItems = `libritui_task_items_${user?.id}_${resolvedTask.id}_${todayStr}`;

      const savedTime = parseInt(localStorage.getItem(storageKeyTime) || localStorage.getItem(legacyKeyTime) || '0', 10);
      const savedItems = parseInt(localStorage.getItem(storageKeyItems) || localStorage.getItem(legacyKeyItems) || '0', 10);

      const targetSec = resolvedTask.target_seconds || (resolvedTask.estimated_minutes * 60);
      const targetCnt = resolvedTask.target_count || 15;

      const isTaskTime = resolvedTask.type === 'pronunciation' ||
        resolvedTask.type === 'listening' ||
        resolvedTask.type === 'grammar_reading' ||
        resolvedTask.type === 'synced_audiobook';

      let initialTime = savedTime;
      let initialItems = savedItems;

      if (resolvedTask.current_progress && resolvedTask.current_progress > 0) {
        if (isTaskTime) {
          initialTime = Math.max(initialTime, resolvedTask.current_progress);
        } else {
          initialItems = Math.max(initialItems, resolvedTask.current_progress);
        }
      }

      if (resolvedTask.completed) {
        initialTime = Math.max(initialTime, targetSec);
        initialItems = Math.max(initialItems, targetCnt);
      }

      setElapsedSeconds(initialTime);
      setCompletedItemsCount(initialItems);
    }
  }, [dailyProgress, matchingTaskType, user?.id]);

  // 3. Detector de Interatividade & Ociosidade (20s)
  useEffect(() => {
    if (isExcludedRoute || !currentTask) return;

    const handleUserInteraction = () => {
      lastActivityTimestampRef.current = Date.now();
      if (isIdle) {
        setIsIdle(false);
      }
    };

    const interactionEvents = ['pointerdown', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click', 'input'];
    interactionEvents.forEach(evt => {
      window.addEventListener(evt, handleUserInteraction, { passive: true });
    });

    return () => {
      interactionEvents.forEach(evt => {
        window.removeEventListener(evt, handleUserInteraction);
      });
    };
  }, [isExcludedRoute, currentTask, isIdle]);

  // 4. Cronômetro de Tarefas de Tempo (com congelamento após 20s de inatividade)
  const isTimeBased = currentTask ? (
    currentTask.type === 'pronunciation' ||
    currentTask.type === 'listening' ||
    currentTask.type === 'grammar_reading' ||
    currentTask.type === 'synced_audiobook'
  ) : false;

  const targetSeconds = currentTask ? (currentTask.target_seconds || currentTask.estimated_minutes * 60) : 0;
  const targetCount = currentTask ? (currentTask.target_count || 15) : 0;

  useEffect(() => {
    if (isExcludedRoute || !currentTask || !isTimeBased || currentTask.completed) {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      return;
    }

    const planDay = activePlan?.current_day_index || 1;
    const cleanL = (activePlan?.language_code || 'en').toLowerCase().split('-')[0];
    const storageKeyTime = `libritui_task_time_${user?.id}_${cleanL}_${planDay}_${currentTask.id}`;

    timerIntervalRef.current = setInterval(() => {
      const timeSinceActivity = Date.now() - lastActivityTimestampRef.current;
      if (timeSinceActivity >= IDLE_THRESHOLD_MS) {
        setIsIdle(true);
        return; // Pausado por inatividade
      }

      setIsIdle(false);
      setElapsedSeconds(prev => {
        const next = prev + 1;
        try {
          localStorage.setItem(storageKeyTime, String(next));
        } catch (e) {}

        // Sincronizar progresso parcial no plano diário
        if (activePlan) {
          studyPlanService.updateTaskCurrentProgress({
            userId: user?.id,
            langCode: activePlan.language_code,
            planDay: activePlan.current_day_index,
            taskId: currentTask.id,
            currentProgress: next,
            autoCompleteIfTargetReached: true
          }).catch(() => {});
        }

        // Meta atingida!
        if (targetSeconds > 0 && next >= targetSeconds && !currentTask.completed) {
          triggerTaskCompletion();
        }
        return next;
      });
    }, 1000);

    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, [isExcludedRoute, currentTask, isTimeBased, targetSeconds, user?.id, activePlan]);

  // 5. Escutar eventos de itens praticados (para tarefas baseadas em contagem)
  useEffect(() => {
    if (isExcludedRoute || !currentTask || isTimeBased) return;

    const handleItemDone = (e: any) => {
      const detail = e.detail;
      const isMatchingFlashcard = 
        (detail?.type?.startsWith('flashcards_') && currentTask.type?.startsWith('flashcards_'));
      const isMatchingType = detail?.type === currentTask.type || isMatchingFlashcard;

      if (detail && isMatchingType) {
        lastActivityTimestampRef.current = Date.now();
        setIsIdle(false);

        const planDay = activePlan?.current_day_index || 1;
        const cleanL = (activePlan?.language_code || 'en').toLowerCase().split('-')[0];
        const storageKeyItems = `libritui_task_items_${user?.id}_${cleanL}_${planDay}_${currentTask.id}`;

        setCompletedItemsCount(prev => {
          const next = prev + 1;
          try {
            localStorage.setItem(storageKeyItems, String(next));
          } catch (e) {}

          // Persistir progresso parcial no plano diário
          if (activePlan) {
            studyPlanService.updateTaskCurrentProgress({
              userId: user?.id,
              langCode: activePlan.language_code,
              planDay: activePlan.current_day_index,
              taskId: currentTask.id,
              currentProgress: next,
              autoCompleteIfTargetReached: true
            }).catch(() => {});
          }

          if (targetCount > 0 && next >= targetCount && !currentTask.completed) {
            triggerTaskCompletion();
          }
          return next;
        });
      }
    };

    window.addEventListener('study_plan_task_item_done', handleItemDone);
    return () => window.removeEventListener('study_plan_task_item_done', handleItemDone);
  }, [isExcludedRoute, currentTask, isTimeBased, targetCount, user?.id, activePlan]);

  // Função para marcar tarefa concluída com som e animação
  const triggerTaskCompletion = async () => {
    if (!currentTask || !activePlan || !user?.id) return;
    try {
      setJustCompleted(true);
      await studyPlanService.toggleTaskComplete({
        userId: user.id,
        langCode: activePlan.language_code,
        planDay: activePlan.current_day_index,
        taskId: currentTask.id,
        completed: true
      });
      loadPlanAndProgress();
      setTimeout(() => setJustCompleted(false), 5000);
    } catch (e) {
      console.warn('[MissionProgressBar] Erro ao concluir tarefa:', e);
    }
  };

  if (isExcludedRoute || !currentTask) return null;

  // Cálculo da porcentagem atual
  let progressPercent = 0;
  if (currentTask.completed) {
    progressPercent = 100;
  } else if (isTimeBased && targetSeconds > 0) {
    progressPercent = Math.min(100, Math.round((elapsedSeconds / targetSeconds) * 100));
  } else if (!isTimeBased && targetCount > 0) {
    progressPercent = Math.min(100, Math.round((completedItemsCount / targetCount) * 100));
  }

  // Formatação de minutos:segundos
  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  return (
    <aside
      ref={barRef}
      aria-label="Barra de tarefa atual"
      className="fixed top-0 left-0 right-0 z-50 pointer-events-none"
    >
      {/* Botão flutuante para restaurar se estiver minimizada à direita */}
      {isCollapsedRight ? (
        <div className="w-full flex justify-end p-2 pointer-events-none">
          <button
            onClick={() => setIsCollapsedRight(false)}
            aria-label="Expandir barra de tarefas"
            className="p-2.5 rounded-l-2xl bg-surface/90 border-l border-y border-black/[0.06] dark:border-white/[0.06] text-ink backdrop-blur-md hover:text-ink flex items-center gap-1.5 transition-all cursor-pointer active:scale-95 pointer-events-auto"
          >
            <ChevronLeft size={18} strokeWidth={2.5} />
            <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-primary">Tarefa</span>
          </button>
        </div>
      ) : (
        <motion.div
          initial={{ y: -40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -40, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="w-full bg-surface/95 border-b border-black/[0.06] dark:border-white/[0.06] backdrop-blur-md text-ink select-none pointer-events-auto"
        >
          {/* Barra de preenchimento contínua no topo extremo da janela (de ponta a ponta) */}
          <div className="w-full h-[3px] bg-surface/80 relative overflow-hidden">
            <motion.div 
              className={`h-full transition-all duration-500 ${
                currentTask.completed || progressPercent >= 100 
                  ? 'bg-ok shadow-[0_0_12px_rgba(16,185,129,0.9)]' 
                  : isIdle 
                  ? 'bg-warn shadow-[0_0_8px_rgba(251,191,36,0.8)]' 
                  : 'bg-ok shadow-[0_0_10px_rgba(16,185,129,0.8)]'
              }`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          {/* Faixa principal padronizada */}
          <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2 flex items-center justify-between gap-3 text-xs">
            {/* Lado Esquerdo: Indicador de atividade, título 'Tarefa Diária' e seta expansora */}
            <div 
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-2 cursor-pointer hover:opacity-90 transition-opacity"
            >
              {/* Ponto Verde Piscando ou Ícone de Pause */}
              {isIdle ? (
                <div className="flex items-center gap-1 text-[11px] font-normal uppercase tracking-[0.14em] font-mono text-warn">
                  <Pause size={13} strokeWidth={3} />
                  <span className="hidden sm:inline text-[10px]">Pausado</span>
                </div>
              ) : currentTask.completed || progressPercent >= 100 ? (
                <div className="w-4 h-4 rounded-full bg-ok text-surface flex items-center justify-center">
                  <CheckCircle2 size={11} strokeWidth={3} />
                </div>
              ) : (
                <div className="relative flex items-center justify-center w-3 h-3">
                  <span className="w-2.5 h-2.5 rounded-full bg-ok animate-ping absolute opacity-75" />
                  <span className="w-2 h-2 rounded-full bg-ok relative" />
                </div>
              )}

              {/* Texto Limpo 'Tarefa Diária' */}
              <span className="font-normal uppercase font-mono tracking-[0.14em] text-ink text-[11px] sm:text-xs">
                Tarefa Diária
              </span>

              {/* Seta suave para baixo indicando que pode expandir detalhes */}
              <motion.div
                animate={{ rotate: isExpanded ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                className="text-muted"
              >
                <ChevronDown size={14} strokeWidth={2.5} />
              </motion.div>
            </div>

            {/* Lado Direito: Métrica 'x/y', porcentagem e botão de minimizar para a direita */}
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Métrica x/y (itens ou minutos) */}
              <span className="px-2.5 py-1 rounded-lg bg-surface/90 text-ink font-mono font-bold text-[11px] sm:text-xs border border-black/[0.06] dark:border-white/[0.06]">
                {currentTask.completed ? (
                  isTimeBased ? `${formatTime(targetSeconds)} / ${formatTime(targetSeconds)}` : `${targetCount} / ${targetCount}`
                ) : isTimeBased ? (
                  `${formatTime(Math.min(elapsedSeconds, targetSeconds))} / ${formatTime(targetSeconds)}`
                ) : (
                  `${Math.min(completedItemsCount, targetCount)} / ${targetCount}`
                )}
              </span>

              {/* Porcentagem */}
              <span className="text-[11px] sm:text-xs font-semibold text-ok min-w-[32px] text-right">
                {progressPercent}%
              </span>

              {/* Notificação Concluída */}
              {justCompleted && (
                <span className="text-[11px] font-semibold text-ok flex items-center gap-1 animate-pulse">
                  <Sparkles size={12} /> Concluída!
                </span>
              )}

              {/* Botão de minimizar barra para a direita */}
              <button
                onClick={() => setIsCollapsedRight(true)}
                aria-label="Minimizar barra de tarefa para a direita"
                title="Minimizar para a direita"
                className="p-1.5 rounded-lg text-muted hover:text-ink hover:bg-tint-stone transition-all cursor-pointer ml-1"
              >
                <ChevronRight size={16} strokeWidth={2.5} />
              </button>
            </div>
          </div>

          {/* Painel Expansível com Detalhes da Tarefa */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: 'easeInOut' }}
                className="overflow-hidden border-t border-black/[0.06] dark:border-white/[0.06] bg-surface/90"
              >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs">
                  <div>
                    <h4 className="font-semibold text-ink text-sm flex items-center gap-2">
                      <span>🎯 {currentTask.title}</span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-tint-stone text-ink">
                        {currentTask.level}
                      </span>
                    </h4>
                    <p className="text-muted text-xs font-medium mt-0.5">
                      {currentTask.description}
                    </p>
                  </div>

                  <button
                    onClick={() => navigate('/')}
                    className="px-3.5 py-1.5 rounded-xl bg-tint-stone hover:bg-tint-stone text-ink hover:text-ink font-medium text-[10px] transition-all cursor-pointer shrink-0"
                  >
                    Ver Roteiro na Home
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </aside>
  );
};
